import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ScrollText, AlertTriangle, ArrowLeft, Search,
  Loader2, TrendingUp, Euro, Cpu, UsersRound, RefreshCw, SlidersHorizontal, Save, LifeBuoy, Paperclip,
  Zap, Flame, Trash2, Activity, Megaphone, ClipboardList, Plus, X, Bell, Gauge, ExternalLink,
  Film, ChevronDown, ChevronRight, Terminal, Undo2, CreditCard, CheckCircle2, FlaskConical,
  Reply, Send, Gift, Rocket, Instagram,
} from "lucide-react";
import GrowthBoard from "../components/growth/GrowthBoard";
import MarketingFunnel from "../components/MarketingFunnel";
import IgReelsBoard from "../components/igreels/IgReelsBoard";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import api, { apiErr, fmtCredits, isAdminTestModeForced, setAdminTestModeForced } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const eur = (n) => `${(n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const eurp = (n) => `${(n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €`;
const usdp = (n) => `$${(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`;
// Tipos de cobro (action_logs.kind). Deben coincidir EXACTAMENTE con lo que escribe
// deduct_seconds: aquí ponía "broll-verify" con guion cuando el tipo real lleva guion bajo, así
// que el segundo mayor consumidor del sistema —28.265 cobros, 110.698 créditos— se mostraba con
// su nombre interno en vez de con su etiqueta. Faltaban además "short" y "montaje", que
// simplemente no aparecían identificados.
const KIND_LABELS = {
  script: "Guion", render: "Render", calendar: "Calendario", analyze: "Análisis",
  recommendations: "Recomendaciones", thumbnail: "Miniatura", narration_premium: "Narración premium",
  broll_ia_image: "B-roll con IA", broll_verify: "Verificación B-roll",
  montaje: "Plan de montaje", short: "Short",
  anotacion_pos: "Posición de anotación", other: "Otro",
};
const RENDER_STATUS_COLOR = {
  completo: "text-emerald-600", rendering: "text-amber-600", error: "text-rose-600",
};
const RENDER_STATUS_LABEL = {
  completo: "Completo", rendering: "Renderizando", error: "Error", idle: "Sin renderizar",
};
// Actividad de fondo que un despliegue podría interrumpir, más allá de render.status (ver
// admin_list_videos en el backend — un one-click puede llevar minutos trabajando con
// render.status todavía en "idle", y un short en curso ni pasa por `render`).
const ACTIVITY_LABEL = {
  one_click: {
    script: "Escribiendo guion…", narration: "Grabando narración…", media: "Buscando material…",
    composing: "Componiendo…", awaiting_approval: "Esperando aprobación",
  },
  short: "Generando short…",
};

const TABS = [
  { id: "metrics", label: "Métricas", Icon: LayoutDashboard },
  { id: "funnel", label: "Embudo de ventas", Icon: Megaphone },
  { id: "markup", label: "Markup IA", Icon: SlidersHorizontal },
  { id: "plans", label: "Planes", Icon: Euro },
  { id: "stripe", label: "Precios Stripe", Icon: CreditCard },
  { id: "ai-usage", label: "Saldo IA", Icon: Gauge },
  { id: "topups", label: "Recargas", Icon: Zap },
  { id: "seats", label: "Asientos", Icon: Flame },
  { id: "logros", label: "Premios", Icon: Gift },
  { id: "referidos", label: "Referidos", Icon: UsersRound },
  { id: "users", label: "Usuarios", Icon: Users },
  { id: "videos", label: "Vídeos", Icon: Film },
  { id: "logs", label: "Transacciones", Icon: ScrollText },
  { id: "activity", label: "Actividad", Icon: Activity },
  { id: "errors", label: "Errores de render", Icon: AlertTriangle },
  { id: "tickets", label: "Tickets de soporte", Icon: LifeBuoy },
  { id: "growth", label: "Growth", Icon: Rocket },
  { id: "kanban-marketing", label: "Kanban marketing", Icon: Megaphone },
  { id: "kanban-dev", label: "Kanban desarrollo", Icon: ClipboardList },
  { id: "prompts", label: "Prompts", Icon: Terminal },
  { id: "igreels", label: "Reels IG", Icon: Instagram },
];

const KANBAN_COLUMN_LABELS = { todo: "Por hacer", doing: "Haciendo", done: "Hecho" };
const KANBAN_COLUMN_ORDER = ["todo", "doing", "done"];

const ACCENT_HEX = { blue: "#2563eb", green: "#10b981", red: "#f43f5e", amber: "#d97706", slate: "#64748b", purple: "#7c3aed" };

function MetricCard({ Icon, label, value, sub, accent = "blue", trend }) {
  const colors = {
    blue: "text-blue-600 bg-blue-50", green: "text-emerald-600 bg-emerald-50",
    red: "text-rose-600 bg-rose-50", amber: "text-amber-600 bg-amber-50",
    slate: "text-slate-600 bg-slate-100", purple: "text-violet-600 bg-violet-50",
  }[accent];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`metric-${label}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg ${colors}`}><Icon size={20} /></span>
          <div>
            <div className="text-2xl font-bold text-slate-900">{value}</div>
            <div className="text-sm text-slate-500">{label}</div>
          </div>
        </div>
        {trend && trend.length > 1 && (
          <div className="h-10 w-20 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend.map((v) => ({ v }))}>
                <Line type="monotone" dataKey="v" stroke={ACCENT_HEX[accent]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {sub && <div className="mt-2 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

const AI_PROVIDER_RECHARGE_URLS = {
  anthropic: "https://console.anthropic.com/settings/billing",
  gemini: "https://aistudio.google.com/app/billing",
  elevenlabs: "https://elevenlabs.io/app/subscription",
  ai33: "https://ai33.pro/app/buy-credits",
};

function AiConsumptionPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/ai-usage").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  const el = data?.providers.find((p) => p.provider === "elevenlabs");
  const donutPct = el && el.characters_limit
    ? Math.min(100, Math.round((el.characters_used / el.characters_limit) * 100)) : null;
  const donutData = donutPct != null ? [{ v: donutPct }, { v: 100 - donutPct }] : [{ v: 0 }, { v: 1 }];
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="ai-consumption-panel">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Consumo de IA</h3>
        <span className="text-xs text-slate-400">Saldo en tiempo real de cada proveedor</span>
      </div>
      <div className="space-y-4">
        <div className="relative mx-auto h-28 w-28 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={donutData} dataKey="v" innerRadius={38} outerRadius={52} startAngle={90} endAngle={-270} stroke="none">
                <Cell fill="#2563eb" />
                <Cell fill="#e2e8f0" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <div className="text-center">
              <div className="text-lg font-bold text-slate-900">{donutPct != null ? `${donutPct}%` : "—"}</div>
              <div className="text-[10px] text-slate-400">Voz usada</div>
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {(data?.providers || []).map((p) => (
            <div key={p.provider} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`ai-consumption-${p.provider}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{AI_PROVIDER_LABELS[p.provider] || p.provider}</div>
                <div className="truncate text-xs text-slate-400" title={p.note || ""}>
                  {!p.configured ? "Sin key configurada"
                    : p.characters_limit ? `${p.characters_remaining.toLocaleString("es-ES")} caract. restantes`
                    : p.error ? `Error: ${p.error}`
                    : p.note || "Saldo no expuesto por API"}
                </div>
              </div>
              <a href={AI_PROVIDER_RECHARGE_URLS[p.provider]} target="_blank" rel="noopener noreferrer"
                 className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-blue-600 hover:underline">
                Recargar <ExternalLink size={12} />
              </a>
            </div>
          ))}
          {!data && <Loader2 className="animate-spin text-blue-600" size={20} />}
        </div>
      </div>
    </div>
  );
}

function MetricsTab() {
  const [m, setM] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/metrics").then(({ data }) => setM(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!m) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  const revenueTrend = m.daily.map((d) => d.revenue);
  const profitTrend = m.daily.map((d) => d.profit);
  const costTrend = m.daily.map((d) => d.cost);
  return (
    <div className="space-y-6" data-testid="admin-metrics">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <MetricCard Icon={TrendingUp} label="MRR" value={eur(m.mrr_eur)} sub="Ingreso recurrente mensual" accent="green" trend={revenueTrend} />
        <MetricCard Icon={Euro} label="Beneficio neto" value={eur(m.profit_eur)} sub={`Ingresos ${eur(m.revenue_eur)} − coste IA`} accent="blue" trend={profitTrend} />
        <MetricCard Icon={Cpu} label="Coste IA" value={eur(m.cost_eur)} sub={`${m.total_actions} acciones · markup x${m.ai_markup}`} accent="red" trend={costTrend} />
        <MetricCard Icon={UsersRound} label="Usuarios" value={m.total_users} accent="slate" />
        <MetricCard Icon={AlertTriangle} label="Errores de render" value={m.render_errors} accent="amber" />
        <MetricCard Icon={RefreshCw} label="Tipo de cambio" value={`${(m.fx_usd_eur ?? 0).toFixed(4)}`} sub="USD → EUR" accent="purple" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">Rentabilidad últimos 30 días</h3>
            {m.daily.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">Aún no hay transacciones registradas.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={m.daily} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Bar dataKey="revenue" name="Ingresos" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="cost" name="Coste IA" fill="#f43f5e" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="profit" name="Beneficio" fill="#2563eb" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-semibold text-slate-700">Usuarios por plan</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {m.users_by_plan.map((p) => (
                <div key={p.plan} className="rounded-lg bg-slate-50 p-4 text-center" data-testid={`plan-count-${p.plan}`}>
                  <div className="text-2xl font-bold text-slate-900">{p.count}</div>
                  <div className="text-sm font-medium text-slate-600">{p.label}</div>
                  <div className="text-xs text-slate-400">{eur(p.price)}/mes</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="xl:col-span-1">
          <AiConsumptionPanel />
        </div>
      </div>
    </div>
  );
}

const AI_PROVIDER_LABELS = {
  anthropic: "Anthropic (Claude)", gemini: "Gemini (imágenes)",
  elevenlabs: "ElevenLabs (voz, directo)", ai33: "ai33.pro (voz, revendedor)",
};

function AiUsageCard({ p }) {
  const label = AI_PROVIDER_LABELS[p.provider] || p.provider;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`ai-usage-${p.provider}`}>
      <h3 className="mb-3 text-sm font-semibold text-slate-700">{label}</h3>
      {!p.configured ? (
        <p className="text-sm text-slate-400">Sin API key configurada en el servidor.</p>
      ) : p.error ? (
        <p className="text-sm text-rose-600">Error consultando el saldo: {p.error}</p>
      ) : p.characters_limit ? (
        <div>
          <div className="mb-1 flex items-baseline justify-between text-sm">
            <span className="font-medium text-slate-900">
              {p.characters_remaining.toLocaleString("es-ES")} caracteres restantes
            </span>
            <span className="text-slate-400">de {p.characters_limit.toLocaleString("es-ES")}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-600"
                 style={{ width: `${Math.min(100, Math.round((p.characters_used / p.characters_limit) * 100))}%` }} />
          </div>
          {p.tier && <div className="mt-2 text-xs text-slate-400">Plan: {p.tier}</div>}
        </div>
      ) : (
        <p className="text-sm text-slate-500">{p.note || "Configurada."}</p>
      )}
    </div>
  );
}

function AiUsageTab() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback((force = false) => {
    if (force) setRefreshing(true);
    api.get("/admin/ai-usage", { params: force ? { force: true } : {} })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setRefreshing(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  return (
    <div className="space-y-4" data-testid="admin-ai-usage">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Actualizado: {new Date(data.checked_at).toLocaleString("es-ES")}</p>
        <button onClick={() => load(true)} disabled={refreshing} data-testid="ai-usage-refresh"
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {data.providers.map((p) => <AiUsageCard key={p.provider} p={p} />)}
      </div>
      <p className="text-xs text-slate-400">
        Anthropic y Gemini no exponen saldo/uso a través de una API key normal — hay que consultar
        console.anthropic.com y aistudio.google.com directamente. Solo ElevenLabs devuelve caracteres
        restantes vía API.
      </p>
    </div>
  );
}

// Los importes que se COBRAN viven en Stripe; PLANS (backend) solo decide lo que enseña la web.
// Cuando se separan, la landing anuncia un precio y Stripe cobra otro — un fallo caro y del que
// nadie se entera. Esta pestaña enseña las dos cifras juntas y permite alinearlas sin entrar en
// el panel de Stripe ni tocar el .env del servidor.
const PERIOD_LABEL = { monthly: "Mensual", annual: "Anual" };

function StripeModeCard({ modo, info, onSync, syncing }) {
  const esLive = modo === "live";
  const filas = info?.precios || [];
  const desalineados = filas.filter((f) => !f.cuadra).length;
  return (
    <div className={`rounded-xl border bg-white p-6 shadow-sm ${esLive ? "border-amber-300" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
            {esLive ? "Producción (live)" : "Pruebas (test)"}
            {esLive && <span className="rounded bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">dinero real</span>}
          </h3>
          <p className="mt-1 max-w-lg text-sm text-slate-500">
            {!info?.tiene_clave
              ? "No hay clave de Stripe configurada para este modo."
              : desalineados === 0
                ? "Stripe cobra exactamente lo que anuncia la web."
                : `${desalineados} de ${filas.length} precios no coinciden con lo que anuncia la web.`}
          </p>
        </div>
        {info?.tiene_clave && desalineados > 0 && (
          <button onClick={() => onSync(modo)} disabled={syncing}
                  data-testid={`stripe-sync-${modo}`}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${
                    esLive ? "bg-amber-600 hover:bg-amber-700" : "bg-blue-600 hover:bg-blue-700"}`}>
            {syncing ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
            Crear precios en Stripe
          </button>
        )}
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="pb-2 pr-4 font-semibold">Plan</th>
              <th className="pb-2 pr-4 font-semibold">Periodo</th>
              <th className="pb-2 pr-4 font-semibold">Anuncia la web</th>
              <th className="pb-2 pr-4 font-semibold">Cobra Stripe</th>
              <th className="pb-2 pr-4 font-semibold">Price ID</th>
              <th className="pb-2 font-semibold">Estado</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((f) => (
              <tr key={`${f.plan}-${f.period}`} className="border-b border-slate-100 last:border-0">
                <td className="py-2.5 pr-4 font-semibold text-slate-800">{f.label}</td>
                <td className="py-2.5 pr-4 text-slate-600">{PERIOD_LABEL[f.period] || f.period}</td>
                <td className="py-2.5 pr-4 tabular-nums text-slate-800">{eur(f.esperado_cents / 100)}</td>
                <td className={`py-2.5 pr-4 tabular-nums ${
                  f.real_cents == null ? "text-slate-400"
                    : f.real_cents === f.esperado_cents ? "text-slate-800" : "text-rose-600 font-semibold"}`}>
                  {f.real_cents == null ? "—" : eur(f.real_cents / 100)}
                </td>
                <td className="py-2.5 pr-4 font-mono text-[11px] text-slate-400">
                  {f.price_id || "sin configurar"}
                  {f.origen === "env" && <span className="ml-1 text-slate-300">(.env)</span>}
                </td>
                <td className="py-2.5">
                  {f.cuadra ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                      <CheckCircle2 size={14} /> Correcto
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"
                          title={f.error || ""}>
                      <AlertTriangle size={14} />
                      {!f.price_id ? "Falta crear" : f.error ? "No existe en Stripe"
                        : f.activo === false ? "Archivado" : "Importe distinto"}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TestModeToggleCard() {
  const [on, setOn] = useState(isAdminTestModeForced());
  const toggle = () => {
    const next = !on;
    setAdminTestModeForced(next);
    setOn(next);
    toast[next ? "success" : "info"](next
      ? "Modo pruebas activado: tus próximos pagos (checkout, portal, cambio de plan, recarga) usarán Stripe en modo test."
      : "Modo pruebas desactivado: tus próximos pagos vuelven a producción.");
  };
  return (
    <div className={`rounded-xl border p-5 flex items-start justify-between gap-4 flex-wrap ${on ? "border-violet-300 bg-violet-50" : "border-slate-200 bg-white"}`}
         data-testid="admin-test-mode-card">
      <div className="flex items-start gap-3">
        <FlaskConical size={22} className={on ? "text-violet-600 shrink-0 mt-0.5" : "text-slate-400 shrink-0 mt-0.5"} />
        <div>
          <h3 className="text-base font-bold text-slate-900">Modo pruebas para tu cuenta</h3>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Con esto activado, TUS checkouts/portal/cambios de plan (con tu usuario admin, en esta web
            en producción) se hacen contra Stripe en modo test, sin dinero real. Solo afecta a tu
            sesión — el resto de usuarios sigue en modo live normal.
          </p>
        </div>
      </div>
      <button onClick={toggle} data-testid="admin-test-mode-toggle"
              className={`shrink-0 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${
                on ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
        {on ? "Activado — desactivar" : "Activar"}
      </button>
    </div>
  );
}

function StripePricesTab() {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/stripe/prices").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const sync = async (modo) => {
    // En live se cobra dinero de verdad, así que no basta con pulsar: hay que escribir LIVE.
    // El backend exige lo mismo por su cuenta (no confía en que el navegador lo haya pedido).
    if (modo === "live") {
      const escrito = window.prompt(
        "Vas a crear los precios REALES de producción en Stripe.\n\n" +
        "Los precios de Stripe son inmutables: se crean nuevos y se archivan los anteriores. " +
        "Las suscripciones ya existentes se quedan en su precio hasta que cambien de plan.\n\n" +
        "Escribe LIVE para confirmar:");
      if ((escrito || "").trim() !== "LIVE") return;
    }
    setSyncing(modo);
    try {
      const { data: r } = await api.post("/admin/stripe/prices/sync",
        { live: modo === "live", confirm: modo === "live" ? "LIVE" : "" });
      const n = (r.creados || []).length;
      if ((r.errores || []).length) {
        toast.error(`${r.errores.length} precio(s) fallaron: ${r.errores[0].error}`);
      } else {
        toast.success(n ? `${n} precio(s) creados en Stripe (${modo}).`
                        : `Ya estaba todo alineado (${modo}).`);
      }
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSyncing(null); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  return (
    <div className="space-y-6" data-testid="admin-stripe">
      <TestModeToggleCard />
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
        <h3 className="text-base font-bold text-slate-900">Precios de Stripe</h3>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          Los precios que se anuncian en la web salen del código; los que se <b>cobran</b> viven en Stripe.
          Aquí se ven juntos y se pueden alinear. Crear un precio no cambia lo que paga nadie hoy:
          los precios de Stripe son inmutables, así que se crea uno nuevo, se archiva el anterior y
          solo afecta a las altas nuevas. Las suscripciones en marcha siguen en su precio hasta que
          cambien de plan.
        </p>
      </div>
      <StripeModeCard modo="test" info={data.test} onSync={sync} syncing={syncing === "test"} />
      <StripeModeCard modo="live" info={data.live} onSync={sync} syncing={syncing === "live"} />
    </div>
  );
}

function MarkupTab() {
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState(3);
  const [saving, setSaving] = useState(false);

  const fetchPreview = useCallback((m) => {
    api.get(`/admin/markup?markup=${m}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    api.get("/admin/markup").then(({ data }) => { setData(data); setMarkup(data.saved_markup); }).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    if (data == null) return;
    const t = setTimeout(() => fetchPreview(markup), 250);
    return () => clearTimeout(t);
  }, [markup, fetchPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/markup", { markup });
      setData(data); setMarkup(data.saved_markup);
      toast.success(`Markup guardado: x${data.saved_markup}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;
  const dirty = Number(markup) !== Number(data.saved_markup);
  const totalProfit = data.examples.reduce((s, e) => s + e.profit_eur, 0);

  return (
    <div className="space-y-6" data-testid="admin-markup">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-base font-bold text-slate-900">Markup de IA</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-lg">
              Multiplicador aplicado sobre el coste real de los modelos para calcular cuántos segundos de
              saldo consume cada acción de IA. A mayor markup, mayor beneficio por acción.
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600" data-testid="markup-value">x{Number(markup).toFixed(1)}</div>
            <div className="text-xs text-slate-400">Guardado: x{Number(data.saved_markup).toFixed(1)}</div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-4">
          <input type="range" min="1" max="10" step="0.5" value={markup} data-testid="markup-slider"
            onChange={(e) => setMarkup(parseFloat(e.target.value))}
            className="flex-1 accent-blue-600" />
          <input type="number" min="1" max="20" step="0.5" value={markup} data-testid="markup-input"
            onChange={(e) => setMarkup(Math.max(1, Math.min(20, parseFloat(e.target.value) || 1)))}
            className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm text-center focus:border-blue-500 focus:outline-none" />
          <button onClick={save} disabled={saving || !dirty} data-testid="markup-save-btn"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
          </button>
        </div>
        {dirty && <p className="mt-2 text-xs text-amber-600">Vista previa sin guardar. Pulsa «Guardar» para aplicarlo a las próximas acciones.</p>}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">Beneficio por funcionalidad · markup x{Number(markup).toFixed(1)}</h3>
          <span className="text-xs text-slate-400">Tarifa Starter · FX {(data.fx_usd_eur ?? 0).toFixed(3)} €/$</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Funcionalidad</th>
                <th className="px-4 py-3 text-right">Saldo consumido</th>
                <th className="px-4 py-3 text-right">Coste IA</th>
                <th className="px-4 py-3 text-right">Cobrado</th>
                <th className="px-4 py-3 text-right">Beneficio</th>
                <th className="px-4 py-3 text-right">Margen</th>
              </tr>
            </thead>
            <tbody>
              {data.examples.map((e) => (
                <tr key={e.label} className="border-t border-slate-100" data-testid={`markup-row-${e.label}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{e.label}</div>
                    <div className="text-[11px] text-slate-400">{e.detail}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500">{e.minutes} min</td>
                  <td className="px-4 py-3 text-right text-rose-600">{eurp(e.cost_eur)}</td>
                  <td className="px-4 py-3 text-right text-emerald-600">{eur(e.revenue_eur)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-600">{eur(e.profit_eur)}</td>
                  <td className="px-4 py-3 text-right text-slate-700">x{e.margin}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-700" colSpan={4}>Beneficio total de los ejemplos</td>
                <td className="px-4 py-3 text-right font-bold text-blue-700" data-testid="markup-total-profit">{eur(totalProfit)}</td>
                <td className="px-4 py-3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-1 text-sm font-semibold text-slate-700">Datos reales medidos · últimos 30 días</h3>
        <p className="mb-4 text-xs text-slate-400">Coste real de Claude por tokens (peor caso, sin descuento de caché). El markup no afecta a estas cifras: reflejan lo realmente consumido y cobrado.</p>
        {(!data.real_stats || data.real_stats.length === 0) ? (
          <p className="py-8 text-center text-sm text-slate-400">Aún no hay acciones de IA registradas en los últimos 30 días.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Acción</th>
                  <th className="px-4 py-3 text-right">Nº</th>
                  <th className="px-4 py-3 text-right">Coste real</th>
                  <th className="px-4 py-3 text-right">Coste medio</th>
                  <th className="px-4 py-3 text-right">Ingreso</th>
                  <th className="px-4 py-3 text-right">Beneficio</th>
                  <th className="px-4 py-3 text-right">Margen real</th>
                  <th className="px-4 py-3 text-right">Tokens (in/out)</th>
                </tr>
              </thead>
              <tbody>
                {data.real_stats.map((r) => (
                  <tr key={r.kind} className="border-t border-slate-100" data-testid={`real-stat-${r.kind}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{KIND_LABELS[r.kind] || r.kind}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{r.count}</td>
                    <td className="px-4 py-3 text-right text-rose-600">{eurp(r.cost_eur)}</td>
                    <td className="px-4 py-3 text-right text-slate-500">{eurp(r.avg_cost_eur)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">{eur(r.revenue_eur)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${r.profit_eur >= 0 ? "text-blue-600" : "text-rose-600"}`}>{eur(r.profit_eur)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.margin != null ? `x${r.margin}` : "—"}</td>
                    <td className="px-4 py-3 text-right text-[11px] text-slate-400">{(r.in_tok || 0).toLocaleString("es-ES")} / {(r.out_tok || 0).toLocaleString("es-ES")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Markup del render de shorts — aparte del de arriba a propósito: el render de un short
          no tiene coste de IA detrás (solo tiempo de servidor/cola), así que es un multiplicador
          de naturaleza distinta y necesita su propia vista previa, no una fila más en la tabla
          de funcionalidades de IA. Detectado el 2026-08-19: un short salía a 29 créditos por
          0,002€ de coste real, sin ningún markup aplicado. */}
      <ShortMarkupCard />
    </div>
  );
}

function ShortMarkupCard() {
  const [data, setData] = useState(null);
  const [markup, setMarkup] = useState(3);
  const [saving, setSaving] = useState(false);

  const fetchPreview = useCallback((m) => {
    api.get(`/admin/short-markup?markup=${m}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    api.get("/admin/short-markup").then(({ data }) => { setData(data); setMarkup(data.saved_markup); }).catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    if (data == null) return;
    const t = setTimeout(() => fetchPreview(markup), 250);
    return () => clearTimeout(t);
  }, [markup, fetchPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/short-markup", { markup });
      setData(data); setMarkup(data.saved_markup);
      toast.success(`Markup de shorts guardado: x${data.saved_markup}`);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-blue-600" size={28} /></div>;
  const dirty = Number(markup) !== Number(data.saved_markup);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-short-markup">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="flex items-center gap-2 text-base font-bold text-slate-900"><Film size={18} className="text-blue-600" /> Markup de render de shorts</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-lg">
            El render de un short no tiene coste de IA detrás (solo tiempo de servidor), así que no lo cubre
            el markup de arriba. Multiplicador sobre los segundos de duración del short.
          </p>
        </div>
        <div className="text-right">
          <div className="text-3xl font-bold text-blue-600" data-testid="short-markup-value">x{Number(markup).toFixed(1)}</div>
          <div className="text-xs text-slate-400">Guardado: x{Number(data.saved_markup).toFixed(1)}</div>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4">
        <input type="range" min="1" max="10" step="0.5" value={markup} data-testid="short-markup-slider"
          onChange={(e) => setMarkup(parseFloat(e.target.value))}
          className="flex-1 accent-blue-600" />
        <input type="number" min="1" max="20" step="0.5" value={markup} data-testid="short-markup-input"
          onChange={(e) => setMarkup(Math.max(1, Math.min(20, parseFloat(e.target.value) || 1)))}
          className="w-20 rounded-lg border border-slate-200 px-3 py-2 text-sm text-center focus:border-blue-500 focus:outline-none" />
        <button onClick={save} disabled={saving || !dirty} data-testid="short-markup-save-btn"
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
        </button>
      </div>
      {dirty && <p className="mt-2 text-xs text-amber-600">Vista previa sin guardar. Pulsa «Guardar» para aplicarlo a los próximos shorts.</p>}

      <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4">
        <p className="text-xs font-semibold uppercase text-slate-400 mb-2">Short típico de {data.duration_sec}s</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
          <div><div className="text-slate-400 text-xs">Render</div><div className="font-semibold text-slate-800">{data.render_credits} cr</div></div>
          <div><div className="text-slate-400 text-xs">Narración</div><div className="font-semibold text-slate-800">{data.narracion_credits} cr</div></div>
          <div><div className="text-slate-400 text-xs">Total</div><div className="font-bold text-blue-700" data-testid="short-markup-total-credits">{data.total_credits} cr · {eur(data.total_eur)}</div></div>
          <div><div className="text-slate-400 text-xs">Coste real</div><div className="font-semibold text-rose-600">{eurp(data.real_cost_eur)}</div></div>
          <div><div className="text-slate-400 text-xs">Margen</div><div className="font-semibold text-slate-700">{data.margin != null ? `x${data.margin}` : "—"}</div></div>
        </div>
      </div>
    </div>
  );
}

// Histórico de cambios de configuración. Con la BD como única fuente, un cambio de precio ya no
// deja rastro en git — este es el único sitio donde queda constancia de quién lo tocó y cuándo.
const CAMPO_LABEL = { topup_packs: "Recargas", plans: "Planes" };

function ConfigHistory({ campo }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/config-history", { params: { limit: 40 } })
      .then(({ data }) => setItems((data.items || []).filter((h) => !campo || h.campo === campo)))
      .catch(() => setItems([]));
  }, [campo]);

  if (!items) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="config-history">
      <h3 className="text-base font-bold text-slate-900">Histórico de cambios</h3>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-slate-400">Todavía no se ha cambiado nada desde aquí.</p>
      ) : (
        <ul className="mt-3 space-y-2 text-sm">
          {items.map((h) => {
            // Solo las claves que de verdad cambiaron: enseñar el objeto entero esconde el cambio.
            const difs = Object.keys(h.despues || {}).flatMap((k) => {
              const a = (h.antes || {})[k] || {}, d = (h.despues || {})[k] || {};
              return Object.keys(d).filter((c) => a[c] !== d[c]).map((c) => `${k}.${c}: ${a[c] ?? "—"} → ${d[c]}`);
            });
            return (
              <li key={h.id} className="border-b border-slate-100 pb-2 last:border-0">
                <div className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">{CAMPO_LABEL[h.campo] || h.campo}</span>
                  <span>{h.por || "—"}</span>
                  <span>{(h.at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-600">
                  {difs.length ? difs.join("  ·  ") : "sin diferencias"}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function PlansTab() {
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/plans").then(({ data }) => { setData(data); setEdits({}); }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const plans = data.plans;
  const cps = data.creditos_por_segundo || 1.2;
  const coste = data.coste_eur_por_credito || 0;
  // Todos los planes se cuentan con la MISMA duración de referencia, para poder compararlos de
  // un vistazo. Los que topan por debajo (Gratis, 5 min) se cuentan con su tope.
  const ref = data.duracion_referencia_min || 15;
  const valueFor = (k, f) => edits[k]?.[f] ?? plans[k][f];
  const setField = (k, f, v) => setEdits((p) => ({ ...p, [k]: { ...p[k], [f]: v } }));
  const dirty = Object.keys(edits).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const body = { plans: Object.fromEntries(Object.entries(edits).map(([k, v]) => [k, {
        price: v.price != null ? Number(v.price) : undefined,
        seconds: v.seconds != null ? Number(v.seconds) : undefined,
        max_minutes: v.max_minutes != null ? Number(v.max_minutes) : undefined,
        channels: v.channels != null ? Number(v.channels) : undefined,
      }])) };
      const { data: r } = await api.post("/admin/plans", body);
      setData(r); setEdits({});
      toast.success("Planes guardados");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Planes de suscripción</h3>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              Viven <b>solo aquí</b>: el código únicamente los siembra en una instalación nueva.
              La columna «Vídeos» cuenta los cuatro planes con la MISMA duración de referencia ({ref} min)
              y con el consumo medido en producción ({cps} créditos por segundo), así que dice lo que
              el plan da <i>de verdad</i> y permite compararlos de un vistazo.
            </p>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Cambiar los créditos <b>no</b> altera lo que consume cada acción — el precio interno
              del crédito es una constante fija. Dar más créditos da más créditos, sin efectos raros.
            </p>
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="plans-save-btn"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-3">Plan</th>
                <th className="px-3 py-3 text-right">€/mes</th>
                <th className="px-3 py-3 text-right">Créditos</th>
                <th className="px-3 py-3 text-right">Máx min/vídeo</th>
                <th className="px-3 py-3 text-right">Canales</th>
                <th className="px-3 py-3 text-right" title="Con la duración de referencia; los planes que topan por debajo se cuentan con su tope">Vídeos</th>
                <th className="px-3 py-3 text-right">Coste</th>
                <th className="px-3 py-3 text-right">Margen</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(plans).map((k) => {
                const price = Number(valueFor(k, "price"));
                const secs = Number(valueFor(k, "seconds"));
                const maxMin = Number(valueFor(k, "max_minutes"));
                const durRef = Math.min(ref, maxMin || ref);
                const vids = durRef > 0 ? secs / (durRef * 60 * cps) : 0;
                const c = secs * coste;
                const margen = price > 0 ? (1 - c / price) * 100 : null;
                const inp = "w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm focus:border-blue-500 focus:outline-none";
                return (
                  <tr key={k} className="border-t border-slate-100" data-testid={`plan-${k}`}>
                    <td className="px-3 py-3 font-semibold text-slate-800">{plans[k].label}</td>
                    <td className="px-3 py-3 text-right">
                      <input type="number" min="0" step="1" value={price} className={inp}
                        data-testid={`plan-price-${k}`} onChange={(e) => setField(k, "price", e.target.value)} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <input type="number" min="0" step="100" value={secs} className={inp}
                        data-testid={`plan-seconds-${k}`} onChange={(e) => setField(k, "seconds", e.target.value)} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <input type="number" min="1" step="1" value={maxMin} className={inp}
                        data-testid={`plan-maxmin-${k}`} onChange={(e) => setField(k, "max_minutes", e.target.value)} />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <input type="number" min="1" step="1" value={Number(valueFor(k, "channels"))} className={inp}
                        data-testid={`plan-channels-${k}`} onChange={(e) => setField(k, "channels", e.target.value)} />
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-700">{vids.toFixed(1)}</td>
                    <td className="px-3 py-3 text-right text-slate-500">{eur(c)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${
                      margen == null ? "text-slate-300" : margen < 50 ? "text-rose-600" : "text-emerald-600"}`}>
                      {margen == null ? "—" : `${margen.toFixed(0)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {dirty && <p className="mt-3 text-xs text-amber-600">Cambios sin guardar. Pulsa «Guardar» para aplicarlos.</p>}
      </div>
      <ConfigHistory campo="plans" />
    </div>
  );
}

function TopupsTab() {
  const [packs, setPacks] = useState(null);
  // Suelo de €/min por debajo del cual recargar sale más barato que suscribirse. Ya no hay
  // "valor del código" con el que comparar: los precios viven solo en BD (ver _sembrar_o_aplicar
  // en el backend), así que no existe una segunda fuente que pueda discrepar en silencio.
  const [umbral, setUmbral] = useState(0);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/topup-packs").then(({ data }) => {
      setPacks(data.packs); setUmbral(data.umbral_eur_min || 0); setEdits({});
    }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!packs) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const valueFor = (key, field) => edits[key]?.[field] ?? packs[key][field];
  const setField = (key, field, val) => setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: val } }));
  const dirty = Object.keys(edits).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const body = { packs: Object.fromEntries(Object.entries(edits).map(([k, v]) => [k, {
        credits: v.seconds != null ? Number(v.seconds) : undefined,
        price: v.price != null ? Number(v.price) : undefined,
      }])) };
      const { data } = await api.post("/admin/topup-packs", body);
      setPacks(data.packs);
      setEdits({});
      load();   // recarga código/umbral para que los avisos reflejen lo recién guardado
      toast.success("Packs de recarga guardados");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-topups">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Packs de recarga de saldo</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-lg">
              Precio y minutos de cada pack de recarga (pago único, solo planes de pago). Se cobra vía
              Stripe con precio dinámico, así que el cambio aplica al instante, sin tocar Stripe.
            </p>
            <p className="mt-2 text-sm text-slate-500 max-w-lg">
              Estos precios viven <b>solo aquí</b>. El código únicamente los siembra la primera vez
              que arranca una instalación nueva; después no se vuelve a consultar, así que no hay
              dos sitios que puedan discrepar. Cada cambio queda registrado en el histórico.
            </p>
          </div>
          <button onClick={save} disabled={saving || !dirty} data-testid="topups-save-btn"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">Pack</th>
                <th className="px-4 py-3 text-right">Créditos</th>
                <th className="px-4 py-3 text-right">Precio (€)</th>
                <th className="px-4 py-3 text-right">€/min</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(packs).map((key) => {
                const credits = Number(valueFor(key, "seconds"));
                const price = Number(valueFor(key, "price"));
                // El suelo compara contra los PLANES, que sí se miden en minutos de vídeo — de ahí la
                // conversión aquí. El pack en sí ya no guarda minutos (ver TOPUP_PACKS).
                const porMin = credits > 0 ? price / (credits / 60) : 0;
                // Por debajo del €/min del plan más caro, recargar sale mejor que suscribirse y
                // nadie sube de plan. Es exactamente lo que llevaba meses pasando sin que se viera.
                const rompeEscalera = porMin > 0 && umbral > 0 && porMin <= umbral;
                return (
                  <tr key={key} className="border-t border-slate-100" data-testid={`topup-pack-${key}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{key}</td>
                    <td className="px-4 py-3 text-right">
                      <input type="number" min="60" step="100" value={credits} data-testid={`topup-credits-${key}`}
                        onChange={(e) => setField(key, "seconds", e.target.value)}
                        className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm focus:border-blue-500 focus:outline-none" />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input type="number" min="0.5" step="0.5" value={price} data-testid={`topup-price-${key}`}
                        onChange={(e) => setField(key, "price", e.target.value)}
                        className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 text-right text-sm focus:border-blue-500 focus:outline-none" />
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold ${rompeEscalera ? "text-rose-600" : "text-slate-700"}`}
                        data-testid={`topup-permin-${key}`}>
                      {porMin > 0 ? porMin.toFixed(3) : "—"}
                      {rompeEscalera && (
                        <span className="ml-1.5 inline-flex items-center" title={`Por debajo de ${umbral.toFixed(3)} €/min, que es lo que cuesta el plan más caro por minuto: recargar sale más barato que subir de plan.`}>
                          <AlertTriangle size={14} />
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {umbral > 0 && (
          <p className="mt-3 text-xs text-slate-500">
            Suelo: <b>{umbral.toFixed(3)} €/min</b> (lo que cuesta por minuto el plan más caro).
            Una recarga por debajo de esa cifra sale más barata que suscribirse.
          </p>
        )}
        {dirty && <p className="mt-3 text-xs text-amber-600">Cambios sin guardar. Pulsa «Guardar» para aplicarlos.</p>}
      </div>
      <ConfigHistory campo="topup_packs" />
    </div>
  );
}

function ReferidosTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/referidos").then(({ data }) => setCfg(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const eur = (cr) => `${(cr * cfg.coste_eur_por_credito).toFixed(2)} €`;
  const setPremio = (k, v) => setCfg((c) => ({ ...c, premios: { ...c.premios, [k]: Math.max(0, Number(v)) } }));

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/referidos", { premios: cfg.premios, bienvenida: cfg.bienvenida });
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Referidos guardados — ya están activos, sin reiniciar");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-referidos">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h3 className="text-base font-bold text-slate-900">Programa de referidos</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Créditos que gana quien refiere, según el plan que contrate el referido. Se pagan
              cuando el referido PAGA, nunca al registrarse, y una sola vez por persona aunque
              luego cambie de plan. Los cambios se aplican en caliente.
            </p>
          </div>
          <button onClick={guardar} disabled={saving} data-testid="referidos-save-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} Guardar
          </button>
        </div>

        <div className="space-y-2">
          {Object.keys(cfg.premios || {}).map((k) => (
            <div key={k} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="w-24 font-semibold text-slate-700 capitalize">{k}</span>
              <input type="number" min={0} value={cfg.premios[k]} onChange={(e) => setPremio(k, e.target.value)}
                className="w-28 rounded-lg border border-slate-300 px-2 py-1.5" />
              <span className="text-slate-400">créditos · nos cuesta {eur(cfg.premios[k])}</span>
            </div>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-slate-700">Bienvenida para el referido:</span>
          <input type="number" min={0} value={cfg.bienvenida} data-testid="referidos-bienvenida"
            onChange={(e) => setCfg((c) => ({ ...c, bienvenida: Math.max(0, Number(e.target.value)) }))}
            className="w-28 rounded-lg border border-slate-300 px-2 py-1.5" />
          <span className="text-slate-400">créditos · nos cuesta {eur(cfg.bienvenida)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <div className="text-2xl font-extrabold text-emerald-900 tabular-nums">{cfg.conversiones}</div>
          <div className="text-sm font-semibold text-emerald-700">referidos que pagaron</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{cfg.pendientes}</div>
          <div className="text-sm font-semibold text-slate-600">registrados sin contratar</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-2xl font-extrabold text-slate-900 tabular-nums">{cfg.total_pagado}</div>
          <div className="text-sm font-semibold text-slate-600">créditos pagados · {eur(cfg.total_pagado)}</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-bold text-slate-900 mb-3">Quién ha traído a quién</h3>
        {cfg.lista?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">Refirió</th><th className="py-2 pr-4">Referido</th>
                <th className="py-2 pr-4">Plan</th><th className="py-2 pr-4">Créditos</th>
                <th className="py-2">Fecha</th>
              </tr></thead>
              <tbody>
                {cfg.lista.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-4 text-slate-800">{r.referidor}</td>
                    <td className="py-2 pr-4 text-slate-800">{r.referido}</td>
                    <td className="py-2 pr-4 text-slate-500 capitalize">{r.plan}</td>
                    <td className="py-2 pr-4 font-bold text-emerald-700 tabular-nums">+{r.creditos}</td>
                    <td className="py-2 text-slate-400">{String(r.fecha || "").substring(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-500">Todavía no ha pagado ningún referido.</p>
        )}
      </div>
    </div>
  );
}

function LogrosTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/logros").then(({ data }) => setCfg(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const setTramo = (i, campo, v) => setCfg((c) => ({
    ...c, tramos: c.tramos.map((t, j) => (j === i ? (campo === 0 ? [Number(v), t[1]] : [t[0], Number(v)]) : t)),
  }));
  const addTramo = () => setCfg((c) => ({ ...c, tramos: [...c.tramos, [(c.tramos.at(-1)?.[0] || 0) + c.cada * 3, c.tope]] }));
  const delTramo = (i) => setCfg((c) => ({ ...c, tramos: c.tramos.filter((_, j) => j !== i) }));

  // Aviso, no bloqueo: un premio por encima de lo que cobra un vídeo hace que hacer vídeos
  // financie hacer más vídeos. Puede ser una promoción deliberada, así que se avisa y ya.
  const excede = [...cfg.tramos.map((t) => t[1]), cfg.tope].filter((p) => p >= cfg.coste_video_creditos);
  const eur = (cr) => `${(cr * cfg.coste_eur_por_credito).toFixed(2)} €`;
  const pct = (cr) => `${Math.round((100 * cr) / (cfg.cada * cfg.coste_video_creditos))}%`;

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/logros", { cada: cfg.cada, tramos: cfg.tramos, tope: cfg.tope });
      setCfg((c) => ({ ...c, ...data }));
      toast.success("Premios guardados — ya están activos, sin reiniciar");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-logros">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
          <div>
            <h3 className="text-base font-bold text-slate-900">Premios por vídeos completados</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-xl">
              Créditos que se regalan al alcanzar cada hito. Cuentan solo los vídeos TERMINADOS, se
              pagan una única vez y van a la bolsa de recarga (no caducan al renovar el plan). Los
              cambios se aplican en caliente, sin reiniciar.
            </p>
          </div>
          <button onClick={guardar} disabled={saving} data-testid="logros-save-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : null} Guardar
          </button>
        </div>

        <label className="block text-sm font-semibold text-slate-700 mb-1">Un hito cada</label>
        <div className="flex items-center gap-2 mb-6">
          <input type="number" min={1} value={cfg.cada} data-testid="logros-cada"
            onChange={(e) => setCfg((c) => ({ ...c, cada: Math.max(1, Number(e.target.value)) }))}
            className="w-24 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          <span className="text-sm text-slate-500">vídeos completados</span>
        </div>

        <div className="text-sm font-semibold text-slate-700 mb-2">Tramos</div>
        <div className="space-y-2">
          {cfg.tramos.map((t, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-500">hasta el hito</span>
              <input type="number" min={1} value={t[0]} onChange={(e) => setTramo(i, 0, e.target.value)}
                className="w-20 rounded-lg border border-slate-300 px-2 py-1.5" />
              <span className="text-slate-500">paga</span>
              <input type="number" min={0} value={t[1]} onChange={(e) => setTramo(i, 1, e.target.value)}
                className="w-24 rounded-lg border border-slate-300 px-2 py-1.5" />
              <span className="text-slate-400">créditos · nos cuesta {eur(t[1])} · {pct(t[1])} de descuento</span>
              <button onClick={() => delTramo(i)} className="ml-auto text-slate-400 hover:text-rose-600 p-1">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button onClick={addTramo} className="text-sm font-semibold text-blue-600 hover:text-blue-700">+ Añadir tramo</button>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold text-slate-700">De ahí en adelante:</span>
          <input type="number" min={0} value={cfg.tope} data-testid="logros-tope"
            onChange={(e) => setCfg((c) => ({ ...c, tope: Math.max(0, Number(e.target.value)) }))}
            className="w-24 rounded-lg border border-slate-300 px-2 py-1.5" />
          <span className="text-slate-400">créditos · nos cuesta {eur(cfg.tope)} · {pct(cfg.tope)} de descuento permanente</span>
        </div>

        {excede.length > 0 && (
          <div className="mt-5 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="logros-aviso">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              Hay premios de <b>{Math.max(...excede)} créditos</b> o más, y un vídeo cobra unos{" "}
              <b>{cfg.coste_video_creditos}</b>. Por encima de esa cifra hacer vídeos financia hacer
              más vídeos: el saldo deja de bajar. Puede ser una promoción a propósito, pero conviene
              saberlo.
            </p>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="text-base font-bold text-slate-900 mb-3">Cómo queda</h3>
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 10 }).map((_, k) => {
            const hito = (k + 1) * cfg.cada;
            const tramo = cfg.tramos.find((t) => hito <= t[0]);
            const premio = tramo ? tramo[1] : cfg.tope;
            return (
              <span key={hito} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm">
                <Gift size={14} className="text-emerald-500" />
                <b className="text-emerald-900">vídeo {hito}</b>
                <span className="text-emerald-700">+{premio}</span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SeatsTab() {
  const [data, setData] = useState(null);
  const [limits, setLimits] = useState({});
  const [closures, setClosures] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/plan-seats").then(({ data }) => { setData(data); setLimits({}); setClosures({}); }).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-600" size={36} /></div>;

  const limitFor = (plan) => limits[plan] ?? data.seats[plan]?.limit ?? 0;
  const closedFor = (plan) => closures[plan] ?? data.seats[plan]?.forced_closed ?? false;
  const dirty = Object.keys(limits).length > 0 || Object.keys(closures).length > 0;

  const saveLimits = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.post("/admin/plan-seats/limits", { limits, forced_closed: closures });
      setData((prev) => ({ ...prev, seats: res.seats }));
      setLimits({});
      setClosures({});
      toast.success("Cupo de asientos guardado");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const removeFromWaitlist = async (id) => {
    try {
      await api.delete(`/admin/plan-seats/waitlist/${id}`);
      setData((prev) => ({ ...prev, waitlist: prev.waitlist.filter((w) => w.id !== id) }));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-seats">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900">Cupo semanal de altas nuevas</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-lg">
              Escasez de marketing: número de altas nuevas por plan y semana (registro gratis o primera
              contratación de un plan de pago). No afecta a quien ya está dentro ni a cambios de plan.
            </p>
          </div>
          <button onClick={saveLimits} disabled={saving || !dirty} data-testid="seats-save-btn"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-40">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Guardar
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Object.entries(data.seats).map(([plan, s]) => (
            <div key={plan} className="rounded-lg bg-slate-50 p-4" data-testid={`seat-plan-${plan}`}>
              <div className="text-sm font-semibold text-slate-700 capitalize mb-2">{plan}</div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-400">Cupo/semana</span>
                <input type="number" min="0" step="1" value={limitFor(plan)} data-testid={`seat-limit-${plan}`}
                  onChange={(e) => setLimits((prev) => ({ ...prev, [plan]: e.target.value }))}
                  className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-sm focus:border-blue-500 focus:outline-none" />
              </div>
              <div className="text-xs text-slate-500">Usado: <b className="text-slate-700">{s?.used ?? 0}</b> · Quedan: <b className={s?.remaining === 0 ? "text-red-600" : "text-emerald-600"}>{s?.remaining ?? 0}</b></div>
              <label className="mt-3 flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={closedFor(plan)} disabled={saving}
                  data-testid={`seat-closed-${plan}`}
                  onChange={(e) => setClosures((prev) => ({ ...prev, [plan]: e.target.checked }))} />
                Mostrar sin plazas disponibles
              </label>
              {closedFor(plan) && <p className="mt-2 text-xs text-slate-500">
                Se abrirán el {fmtDateShort(s?.reopens_at || s?.week_ends_at)} según el cupo configurado.
              </p>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold text-slate-700">Lista de espera ({data.waitlist.length})</h3>
        {data.waitlist.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">Nadie en lista de espera ahora mismo.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Semana</th>
                  <th className="px-4 py-3">Fecha de inscripción</th>
                  <th className="px-4 py-3">Registro en la plataforma</th>
                  <th className="px-4 py-3">Fecha prevista de aviso</th>
                  <th className="px-4 py-3">Correo de apertura</th>
                  <th className="px-4 py-3">Compra posterior</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {data.waitlist.map((w) => (
                  <tr key={w.id} className="border-t border-slate-100" data-testid={`waitlist-row-${w.id}`}>
                    <td className="px-4 py-3 text-slate-800">{w.email}</td>
                    <td className="px-4 py-3 text-slate-600 capitalize">{w.plan}</td>
                    <td className="px-4 py-3 text-slate-400">{w.week}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateShort(w.created_at) || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {w.registration ? <>
                        {w.registration.existed_when_joined ? "Ya tenía cuenta" : "Registrado"}
                        <div className="mt-1 whitespace-nowrap">{fmtDateShort(w.registration.registered_at) || "—"}</div>
                      </> : "Sin cuenta"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-500">{fmtDateShort(w.notify_at) || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {w.notice_status === "sent" ? `Enviado · ${fmtDateShort(w.notice_sent_at)}`
                        : w.notice_status === "sending" ? "Enviando"
                        : w.notice_status === "retry" ? "Pendiente de reintento"
                        : w.notice_status === "uncertain" ? "Comprobar envío"
                        : w.notice_status === "skipped_purchased" ? "No necesario: compró"
                        : w.notice_status === "skipped_subscribed" ? "Ya tiene un plan"
                        : "Pendiente"}
                    </td>
                    <td className="px-4 py-3 text-xs" data-testid={`waitlist-purchase-${w.id}`}>
                      {w.purchase ? <>
                        <span className="font-semibold text-emerald-700">Compró · <span className="capitalize">{w.purchase.plan}</span></span>
                        <div className="mt-1 whitespace-nowrap text-slate-500">{fmtDateShort(w.purchase.purchased_at)}</div>
                      </> : <span className="text-slate-400">Sin compra posterior</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => removeFromWaitlist(w.id)} data-testid={`waitlist-remove-${w.id}`}
                        className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Mismo formato que AdminUserPage.jsx (fecha de alta / próxima renovación en la ficha
// individual), para que el listado y la ficha se lean igual.
const fmtDateShort = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return null; }
};

function UsersTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState("");
  const [page, setPage] = useState(1);
  // Los borrados no salen por defecto (ver admin_list_users) — este interruptor los trae para
  // poder restaurarlos desde su ficha.
  const [showDeleted, setShowDeleted] = useState(false);
  const load = useCallback(() => {
    const params = new URLSearchParams({ q, plan, page: String(page), limit: "25" });
    if (showDeleted) params.set("include_deleted", "true");
    api.get(`/admin/users?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, [q, plan, page, showDeleted]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por email o nombre…" data-testid="admin-user-search"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        <select value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }}
          data-testid="admin-plan-filter"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Todos los planes</option>
          <option value="gratis">Gratis</option><option value="starter">Starter</option>
          <option value="creator">Creator</option><option value="pro">Pro</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <input type="checkbox" checked={showDeleted} data-testid="admin-show-deleted"
            onChange={(e) => { setShowDeleted(e.target.checked); setPage(1); }}
            className="h-4 w-4 accent-slate-600" />
          Ver borrados
        </label>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Usuario</th><th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Lista de espera</th>
              <th className="px-4 py-3">Minutos</th>
              <th className="px-4 py-3">Alta</th><th className="px-4 py-3">Renovación</th>
              <th className="px-4 py-3">Estado</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></td></tr>
            ) : data.users.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400">Sin resultados.</td></tr>
            ) : data.users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="font-medium text-slate-900">{u.name || "—"}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{u.plan_label}</span>
                  {u.pending_plan_change && <div className="mt-1 text-[11px] text-amber-600">→ {u.pending_plan_change.plan}</div>}
                </td>
                <td className="px-4 py-3" data-testid={`admin-user-waitlist-${u.id}`}>
                  {u.waitlist_plans?.length > 0 ? <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                    En espera · <span className="capitalize">{u.waitlist_plans.join(", ")}</span>
                  </span> : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-700">{fmtCredits((u.plan_seconds || 0) + (u.topup_seconds || 0))} créditos
                  <div className="text-[11px] text-slate-400">plan {fmtCredits(u.plan_seconds)} · recarga {fmtCredits(u.topup_seconds)}</div>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDateShort(u.created_at) || "—"}</td>
                {/* Solo tiene renovación quien tiene (o tuvo) un plan de pago activo — gratis
                    nunca la tiene, plan_grant_at no se pone hasta el primer pago.
                    OJO: plan_grant_at es solo "fin del periodo actual" — NO distingue si después
                    Stripe vuelve a cobrar o el usuario cae a gratis. Cuando ya hay una baja
                    programada (pending_plan_change.plan === "gratis", la misma marca que ya se
                    muestra en ámbar bajo Plan como "→ gratis"), esa fecha es la que se ACABA el
                    plan, no la que se renueva — mostrarla sin más decía justo lo contrario de lo
                    que iba a pasar (bug real: Axión Mental, ya de baja, aparecía "renovando"). */}
                <td className="px-4 py-3 text-xs">
                  {u.payment_overdue ? <span className="font-medium text-rose-600">Pago pendiente</span> : u.pending_plan_change?.plan === "gratis" ? (
                    <span className="text-rose-600 font-medium">Cancela: {fmtDateShort(u.plan_renews_at) || "—"}</span>
                  ) : (
                    <span className="text-slate-500">{fmtDateShort(u.plan_renews_at) || "—"}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.deleted && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">Borrado</span>}
                  {u.role === "admin" && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">Admin</span>}
                  {u.suspended && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs text-rose-700">Suspendido</span>}
                  {u.payment_overdue && <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">Impago</span>}
                  {u.role !== "admin" && !u.suspended && !u.deleted && !u.payment_overdue && <span className="text-xs text-emerald-600">Activo</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => navigate(`/admin/users/${u.id}`)} data-testid={`admin-user-open-${u.id}`}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                    Gestionar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Anterior</button>
          <span className="text-slate-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Siguiente</button>
        </div>
      )}
    </div>
  );
}

/**
 * Registro global de TODOS los vídeos de TODOS los usuarios — el mismo desglose de coste/
 * ingreso/beneficio por concepto que ya existía dentro de un perfil de usuario (AdminUserPage,
 * sección "Canales"), pero sin tener que entrar usuario a usuario para verlo.
 */
function VideosTab() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [renderStatus, setRenderStatus] = useState("");
  const [page, setPage] = useState(1);
  const [openVideo, setOpenVideo] = useState(null);
  const load = useCallback(() => {
    const params = new URLSearchParams({ q, render_status: renderStatus, page: String(page), limit: "30" });
    api.get(`/admin/videos?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, [q, renderStatus, page]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <div className="space-y-4" data-testid="admin-videos">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Buscar por nombre del vídeo o email del usuario…" data-testid="admin-video-search"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none" />
        </div>
        <select value={renderStatus} onChange={(e) => { setRenderStatus(e.target.value); setPage(1); }}
          data-testid="admin-video-status-filter"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Todos los estados</option>
          <option value="completo">Completo</option>
          <option value="rendering">Renderizando</option>
          <option value="error">Error</option>
          <option value="idle">Sin renderizar</option>
        </select>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Vídeo</th><th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Canal</th><th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Ingreso</th><th className="px-4 py-3 text-right">Coste</th>
              <th className="px-4 py-3 text-right">Beneficio</th><th className="px-4 py-3 text-right">Créditos</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={9} className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></td></tr>
            ) : data.videos.length === 0 ? (
              <tr><td colSpan={9} className="py-12 text-center text-slate-400">Sin resultados.</td></tr>
            ) : data.videos.map((v) => {
              const vOpen = openVideo === v.id;
              const hasCost = (v.cost || 0) > 0 || (v.revenue || 0) > 0 || (v.credits || 0) > 0;
              return (
                <Fragment key={v.id}>
                  <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-video-row-${v.id}`}>
                    <td className="px-4 py-3">
                      <button disabled={!hasCost} onClick={() => setOpenVideo(vOpen ? null : v.id)}
                        className={`flex min-w-0 items-center gap-1.5 text-left ${hasCost ? "cursor-pointer" : "cursor-default"}`}>
                        {hasCost && (vOpen ? <ChevronDown size={13} className="shrink-0 text-slate-400" /> : <ChevronRight size={13} className="shrink-0 text-slate-400" />)}
                        <span className="truncate font-medium text-slate-900">{v.name || "Vídeo"}</span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      <div className="text-slate-700">{v.user?.name || "—"}</div>
                      <div>{v.user?.email || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{v.channel_name || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${RENDER_STATUS_COLOR[v.render_status] || "text-slate-400"}`}>
                        {RENDER_STATUS_LABEL[v.render_status] || v.render_status || v.status || "—"}
                      </span>
                      {v.activity?.kind === "one_click" && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-600" title="Actividad en curso — un despliegue podría interrumpirla">
                          <Loader2 size={11} className="animate-spin" />
                          {ACTIVITY_LABEL.one_click[v.activity.step] || "Produciendo en 1 clic…"}
                        </div>
                      )}
                      {v.activity?.kind === "short" && (
                        <div className="mt-0.5 flex items-center gap-1 text-xs font-medium text-amber-600" title="Actividad en curso — un despliegue podría interrumpirla">
                          <Loader2 size={11} className="animate-spin" />
                          {ACTIVITY_LABEL.short}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-emerald-600">{eur(v.revenue)}</td>
                    <td className="px-4 py-3 text-right text-xs text-rose-600">{eur(v.cost)}</td>
                    <td className="px-4 py-3 text-right text-xs font-semibold text-blue-600">{eur(v.profit)}</td>
                    <td className="px-4 py-3 text-right text-xs text-slate-500">{fmtCredits(v.credits || 0)}</td>
                    <td className="px-4 py-3 text-right">
                      {v.user?.id && (
                        <button onClick={() => navigate(`/admin/users/${v.user.id}`)} data-testid={`admin-video-open-user-${v.id}`}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100">
                          Ver usuario
                        </button>
                      )}
                    </td>
                  </tr>
                  {vOpen && hasCost && (
                    <tr className="border-t border-slate-100 bg-slate-50">
                      <td colSpan={9} className="px-4 py-2">
                        <table className="w-full text-xs">
                          <thead className="text-left uppercase text-slate-400">
                            <tr><th className="py-1">Concepto</th><th className="py-1 text-right">Ingreso</th>
                              <th className="py-1 text-right">Coste</th><th className="py-1 text-right">Beneficio</th>
                              <th className="py-1 text-right">Créditos</th></tr>
                          </thead>
                          <tbody>
                            {v.by_kind.map((k) => (
                              <tr key={k.kind} className="border-t border-slate-200">
                                <td className="py-1">{KIND_LABELS[k.kind] || k.kind}</td>
                                <td className="py-1 text-right text-emerald-600">{eur(k.revenue)}</td>
                                <td className="py-1 text-right text-rose-600">{eur(k.cost)}</td>
                                <td className="py-1 text-right font-medium text-blue-600">{eur(k.profit)}</td>
                                <td className="py-1 text-right text-slate-500">{fmtCredits(k.credits || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {v.regenerations?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">
                              Original vs. regeneraciones ({v.regenerations.length}×
                              "Regenerar todo automáticamente")
                            </div>
                            <table className="w-full text-xs">
                              <thead className="text-left uppercase text-slate-400">
                                <tr><th className="py-1">Tramo</th><th className="py-1 text-right">Ingreso</th>
                                  <th className="py-1 text-right">Coste</th><th className="py-1 text-right">Beneficio</th>
                                  <th className="py-1 text-right">Créditos</th></tr>
                              </thead>
                              <tbody>
                                <tr className="border-t border-slate-200">
                                  <td className="py-1">Original</td>
                                  <td className="py-1 text-right text-emerald-600">{eur(v.original.revenue)}</td>
                                  <td className="py-1 text-right text-rose-600">{eur(v.original.cost)}</td>
                                  <td className="py-1 text-right font-medium text-blue-600">{eur(v.original.profit)}</td>
                                  <td className="py-1 text-right text-slate-500">{fmtCredits(v.original.credits || 0)}</td>
                                </tr>
                                {v.regenerations.map((r, i) => (
                                  <tr key={r.at} className="border-t border-slate-200">
                                    <td className="py-1">Regeneración {i + 1} <span className="text-slate-400">· {new Date(r.at).toLocaleString("es-ES")}</span></td>
                                    <td className="py-1 text-right text-emerald-600">{eur(r.revenue)}</td>
                                    <td className="py-1 text-right text-rose-600">{eur(r.cost)}</td>
                                    <td className="py-1 text-right font-medium text-blue-600">{eur(r.profit)}</td>
                                    <td className="py-1 text-right text-slate-500">{fmtCredits(r.credits || 0)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Anterior</button>
          <span className="text-slate-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Siguiente</button>
        </div>
      )}
    </div>
  );
}

function LogsTab() {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const load = useCallback(() => {
    const params = new URLSearchParams({ kind, page: String(page), limit: "50" });
    api.get(`/admin/logs?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, [kind, page]);
  useEffect(() => { load(); }, [load]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <div className="space-y-4" data-testid="admin-logs">
      <select value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }}
        data-testid="admin-logs-kind"
        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
        <option value="">Todas las acciones</option>
        {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Usuario</th><th className="px-4 py-3">Acción</th>
              <th className="px-4 py-3 text-right">Seg. descontados</th><th className="px-4 py-3 text-right">Ingreso</th>
              <th className="px-4 py-3 text-right">Coste</th><th className="px-4 py-3 text-right">Beneficio</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={7} className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></td></tr>
            ) : data.logs.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-slate-400">Sin transacciones.</td></tr>
            ) : data.logs.map((l) => (
              <tr key={l.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("es-ES")}</td>
                <td className="px-4 py-2.5 text-slate-700">{l.user_email}</td>
                <td className="px-4 py-2.5">{KIND_LABELS[l.kind] || l.kind}
                  {l.ai_calls > 0 && <div className="text-[11px] text-slate-400">{(l.ai_in_tokens || 0).toLocaleString("es-ES")} / {(l.ai_out_tokens || 0).toLocaleString("es-ES")} tok · {l.ai_calls} llam.</div>}
                </td>
                <td className="px-4 py-2.5 text-right text-slate-500">{l.seconds}</td>
                <td className="px-4 py-2.5 text-right">
                  <span className="text-emerald-600">{eur(l.revenue_eur)}</span>
                  {l.admin_comp && (
                    <span title="Saldo añadido manualmente desde el admin, sin valor en euros — el ingreso 0€ es esperado, no un fallo de cobro."
                      className="ml-1.5 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                      cortesía
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-rose-600">{eurp(l.cost_eur)}</td>
                <td className={`px-4 py-2.5 text-right font-medium ${l.profit_eur >= 0 ? "text-blue-600" : "text-rose-600"}`}>{eur(l.profit_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Anterior</button>
          <span className="text-slate-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Siguiente</button>
        </div>
      )}
    </div>
  );
}

const AI_KIND_LABELS = {
  script: "Guion", improve: "Mejorar guion", concepts: "Resaltes/personajes",
  storyboard: "Storyboard", score: "Evaluación calidad", ideas: "Ideas",
  calendar: "Calendario", "calendar-enrich": "Calendario (detalle)",
  highlights: "Rótulos (extracción IA)", thumbnail: "Miniatura", other: "Otro",
};

function AiLogDetailDialog({ id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!id) return;
    setData(null);
    api.get(`/admin/ai-logs/${id}`).then(({ data }) => setData(data)).catch((e) => { toast.error(apiErr(e)); onClose(); });
  }, [id, onClose]);
  if (!id) return null;
  const Block = ({ label, text, truncated }) => (
    <div>
      <div className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label} {truncated && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">recortado</span>}
      </div>
      <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs text-slate-700">{text || "—"}</pre>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
        {!data ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={32} /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">{AI_KIND_LABELS[data.kind] || data.kind}</h3>
                <p className="text-xs text-slate-400">{new Date(data.created_at).toLocaleString("es-ES")} · {data.session_id}</p>
              </div>
              <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-sm font-bold text-slate-900">{data.model}</div><div className="text-xs text-slate-500">Modelo</div></div>
              <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-sm font-bold text-slate-900">{data.duration_ms} ms</div><div className="text-xs text-slate-500">Latencia</div></div>
              <div className="rounded-lg bg-slate-50 p-3 text-center"><div className="text-sm font-bold text-slate-900">{usdp(data.cost_usd)}</div><div className="text-xs text-slate-500">Coste (USD)</div></div>
              <div className="rounded-lg bg-slate-50 p-3 text-center"><div className={`text-sm font-bold ${data.status === "error" ? "text-rose-600" : "text-emerald-600"}`}>{data.status}</div><div className="text-xs text-slate-500">Estado</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-slate-500">
              <div>Entrada: <b className="text-slate-700">{(data.input_tokens || 0).toLocaleString("es-ES")}</b> tok</div>
              <div>Salida: <b className="text-slate-700">{(data.output_tokens || 0).toLocaleString("es-ES")}</b> tok</div>
              <div>Caché lectura: <b className="text-slate-700">{(data.cache_read_tokens || 0).toLocaleString("es-ES")}</b> tok</div>
              <div>Caché escritura: <b className="text-slate-700">{(data.cache_write_tokens || 0).toLocaleString("es-ES")}</b> tok</div>
            </div>
            {data.error && <div className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{data.error}</div>}
            <Block label="Prompt de sistema" text={data.system_prompt} truncated={data.system_prompt_truncated} />
            <Block label="Prompt de usuario" text={data.user_prompt} truncated={data.user_prompt_truncated} />
            <Block label="Respuesta de la IA" text={data.response_text} truncated={data.response_truncated} />
          </div>
        )}
      </div>
    </div>
  );
}

const EVENT_TYPE_LABELS = {
  narration_started: "Narración iniciada", narration_completed: "Narración completada", narration_failed: "Narración fallida",
  render_started: "Render iniciado", render_completed: "Render completado", render_failed: "Render fallido",
  script_generation_started: "Guion iniciado", script_generation_completed: "Guion completado", script_generation_failed: "Guion fallido",
  one_click_started: "1-clic iniciado", one_click_failed: "1-clic fallido",
  youtube_upload_started: "Subida YouTube iniciada", youtube_upload_completed: "Subida YouTube completada", youtube_upload_failed: "Subida YouTube fallida",
  thumbnail_auto_generated: "Miniatura automática generada", thumbnail_auto_skipped: "Miniatura automática omitida",
  render_clip_failed: "Clip de B-roll fallido", render_overlay_failed: "Rótulo/transición fallidos",
  render_transitions_failed: "Transiciones fallidas", render_subtitles_failed: "Subtítulos fallidos",
  render_music_failed: "Música fallida", render_loudnorm_failed: "Normalizado de audio fallido",
  render_highlights_extraction_failed: "Extracción de rótulos fallida",
  broll_ia_image_generated: "Imagen de B-roll generada con IA", broll_ia_image_failed: "Imagen de B-roll con IA fallida",
  broll_search_cascade: "Búsqueda de B-roll (cascada de fuentes)",
  storyboard_backfill_used: "Storyboard rellenado de respaldo (recuperado)", storyboard_backfill_failed: "Storyboard de respaldo fallido",
};
const ACTIVITY_TYPE_LABEL = (t) => t.startsWith("ai:") ? `IA · ${AI_KIND_LABELS[t.slice(3)] || t.slice(3)}` : (EVENT_TYPE_LABELS[t] || t);
const ACTIVITY_TYPE_COLOR = (l) => l.status === "error" ? "bg-rose-100 text-rose-700"
  : l.source === "ai" ? "bg-indigo-100 text-indigo-700"
  : l.type.endsWith("_started") ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

function ActivityTab() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [projectId, setProjectId] = useState("");
  const [page, setPage] = useState(1);
  const [openAiId, setOpenAiId] = useState(null);
  const load = useCallback(() => {
    const params = new URLSearchParams({ type, status, project_id: projectId, page: String(page), limit: "50" });
    api.get(`/admin/activity?${params}`).then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, [type, status, projectId, page]);
  useEffect(() => { load(); }, [load]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  return (
    <div className="space-y-4" data-testid="admin-activity">
      <p className="text-sm text-slate-500">El journey completo del usuario — narración, render (y por qué falla), YouTube, miniaturas, 1-clic — entrelazado cronológicamente con cada llamada real a la IA (clic en una fila de IA para ver prompt/respuesta completos).</p>
      <div className="flex flex-wrap gap-3">
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }}
          data-testid="admin-activity-type"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Todos los eventos</option>
          {(data?.types || []).map((t) => <option key={t} value={t}>{ACTIVITY_TYPE_LABEL(t)}</option>)}
        </select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
          <option value="">Todos los estados</option>
          <option value="ok">OK</option>
          <option value="error">Error</option>
        </select>
        <input value={projectId} onChange={(e) => { setProjectId(e.target.value); setPage(1); }}
          placeholder="Filtrar por id de proyecto…"
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Fecha</th><th className="px-4 py-3">Evento</th><th className="px-4 py-3">Usuario</th>
              <th className="px-4 py-3">Proyecto</th><th className="px-4 py-3">Detalle</th>
              <th className="px-4 py-3 text-right">Coste</th>
              <th className="px-4 py-3 text-right">Duración</th><th className="px-4 py-3">Estado</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></td></tr>
            ) : data.logs.length === 0 ? (
              <tr><td colSpan={8} className="py-12 text-center text-slate-400">Sin eventos todavía.</td></tr>
            ) : data.logs.map((l) => (
              <tr key={l.id} onClick={() => l.source === "ai" && setOpenAiId(l.id)} data-testid={`activity-row-${l.id}`}
                className={`border-t border-slate-100 ${l.source === "ai" ? "cursor-pointer hover:bg-slate-50" : ""}`}>
                <td className="px-4 py-2.5 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("es-ES")}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ACTIVITY_TYPE_COLOR(l)}`}>{ACTIVITY_TYPE_LABEL(l.type)}</span>
                </td>
                <td className="px-4 py-2.5 text-slate-700">{l.user_email || l.user_id || "—"}</td>
                <td className="px-4 py-2.5 text-slate-700">{l.project_name || l.project_id || "—"}</td>
                <td className="px-4 py-2.5 max-w-[280px] truncate text-xs text-slate-500">
                  {l.status === "error" ? <span className="text-rose-600">{l.error}</span>
                    : l.source === "ai" ? (l.meta.response_preview || "—")
                    : Object.entries(l.meta || {}).map(([k, v]) => `${k}=${v}`).join(" · ")}
                </td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-500">{l.source === "ai" ? usdp(l.meta.cost_usd) : "—"}</td>
                <td className="px-4 py-2.5 text-right text-xs text-slate-500">{l.duration_ms != null ? `${l.duration_ms} ms` : "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.status === "error" ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{l.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Anterior</button>
          <span className="text-slate-500">{page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1.5 disabled:opacity-40">Siguiente</button>
        </div>
      )}
      <AiLogDetailDialog id={openAiId} onClose={() => setOpenAiId(null)} />
    </div>
  );
}

function ErrorsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/render-errors").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  return (
    <div className="space-y-4" data-testid="admin-errors">
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr><th className="px-4 py-3">Proyecto</th><th className="px-4 py-3">Error</th><th className="px-4 py-3">Último log</th></tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={3} className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></td></tr>
            ) : data.errors.length === 0 ? (
              <tr><td colSpan={3} className="py-12 text-center text-slate-400">No hay errores de render. 🎉</td></tr>
            ) : data.errors.map((e) => (
              <tr key={e.project_id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800">{e.name || e.project_id}</td>
                <td className="px-4 py-3 text-rose-600">{e.error || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{e.last_log || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Un prompt individual: colapsado por defecto (muchos son larguísimos), con textarea editable
// solo al expandir. Estado de edición LOCAL (draft) separado del valor guardado en el servidor —
// así "Guardar" queda deshabilitado hasta que el texto realmente cambia, y "Descartar cambios"
// puede volver al valor servido sin llamar a la API.
function PromptCard({ p, onSave, onRevert }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(p.value);
  const [saving, setSaving] = useState(false);
  const [reverting, setReverting] = useState(false);
  const dirty = draft !== p.value;

  const save = async () => {
    setSaving(true);
    try { await onSave(p.key, draft); }
    finally { setSaving(false); }
  };
  const discard = () => setDraft(p.value);
  const revert = async () => {
    if (!window.confirm(`¿Volver "${p.label}" a su versión anterior (o al valor original del código si no hay historial)?`)) return;
    setReverting(true);
    try {
      const next = await onRevert(p.key);
      if (next != null) setDraft(next);
    } finally { setReverting(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`prompt-card-${p.key}`}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div className="flex min-w-0 items-center gap-2.5">
          {open ? <ChevronDown size={16} className="shrink-0 text-slate-400" /> : <ChevronRight size={16} className="shrink-0 text-slate-400" />}
          <span className="truncate font-semibold text-slate-900">{p.label}</span>
          {p.modified && (
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
              Modificado
            </span>
          )}
          {dirty && (
            <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-blue-700">
              Sin guardar
            </span>
          )}
        </div>
        <span className="shrink-0 text-xs text-slate-400">{p.value.length.toLocaleString("es-ES")} caracteres</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {p.placeholders.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
              <span className="font-semibold text-slate-600">Marcadores:</span>
              {p.placeholders.map((ph) => (
                <code key={ph} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
                  {p.syntax === "format" ? `{${ph}}` : `$${ph}`}
                </code>
              ))}
              <span className="text-slate-400">— no los borres, el sistema los rellena en cada llamada.</span>
            </div>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={16}
            spellCheck={false}
            className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-xs leading-relaxed text-slate-800 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-xs text-slate-400">
              {p.updated_at ? `Editado ${new Date(p.updated_at).toLocaleString("es-ES")}${p.updated_by ? ` por ${p.updated_by}` : ""}` : "Sin editar (valor original del código)"}
            </div>
            <div className="flex gap-2">
              {dirty && (
                <button type="button" onClick={discard}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                  Descartar cambios
                </button>
              )}
              {p.modified && (
                <button type="button" onClick={revert} disabled={reverting}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                  {reverting ? <Loader2 size={13} className="animate-spin" /> : <Undo2 size={13} />} Revertir
                </button>
              )}
              <button type="button" onClick={save} disabled={!dirty || saving}
                data-testid={`prompt-save-${p.key}`}
                className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PromptsTab() {
  const [prompts, setPrompts] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/prompts").then(({ data }) => setPrompts(data.prompts)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (key, value) => {
    try {
      await api.put(`/admin/prompts/${key}`, { value });
      toast.success("Prompt actualizado — ya aplica en la siguiente llamada de IA, sin desplegar.");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const handleRevert = async (key) => {
    try {
      const { data } = await api.post(`/admin/prompts/${key}/revert`);
      toast.success("Prompt revertido.");
      load();
      return data.value;
    } catch (e) { toast.error(apiErr(e)); return null; }
  };

  if (!prompts) {
    return <div className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></div>;
  }

  const byCategory = {};
  for (const p of prompts) {
    (byCategory[p.category] ||= []).push(p);
  }

  return (
    <div className="space-y-6" data-testid="admin-prompts">
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        Editar un prompt aquí cambia lo que la IA recibe <b>al instante</b>, sin desplegar código.
        "Revertir" deshace el último cambio guardado (o vuelve al valor original del código si
        nunca hubo un cambio anterior). Cuidado con los marcadores ({"{"}nombre{"}"} o $nombre según
        el prompt) — si los borras, esa parte del prompt no se rellenará con datos reales.
      </div>
      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="space-y-2">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{cat}</h3>
          <div className="space-y-2">
            {items.map((p) => (
              <PromptCard key={p.key} p={p} onSave={handleSave} onRevert={handleRevert} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TicketReplyBox({ tk, onReplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const msg = text.trim();
    if (!msg) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/support-tickets/${tk.id}/reply`, { message: msg });
      toast.success(data.email_sent ? "Respuesta enviada." : "Respuesta guardada, pero el email NO se pudo enviar.");
      setText(""); setOpen(false);
      onReplied(tk.id, data.reply);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!tk.id) return null; // tickets antiguos sin id propio: no se pueden direccionar todavía
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid="admin-ticket-reply-open"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800">
          <Reply size={13} /> Responder
        </button>
      ) : (
        <div className="space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} autoFocus
            placeholder="Escribe la respuesta que recibirá el usuario por email…"
            data-testid="admin-ticket-reply-text"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
          <div className="flex gap-2">
            <button onClick={send} disabled={busy || !text.trim()} data-testid="admin-ticket-reply-send"
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar respuesta
            </button>
            <button onClick={() => { setOpen(false); setText(""); }} disabled={busy}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TicketsTab() {
  const [data, setData] = useState(null);
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
  useEffect(() => {
    api.get("/admin/support-tickets").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e)));
  }, []);

  const onReplied = (tid, reply) => {
    setData((prev) => ({
      ...prev,
      tickets: prev.tickets.map((tk) => tk.id === tid
        ? { ...tk, status: "respondido", thread: [...(tk.thread || []), reply] }
        : tk),
    }));
  };

  return (
    <div className="space-y-4" data-testid="admin-tickets">
      {!data ? (
        <div className="py-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></div>
      ) : data.tickets.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white py-12 text-center text-slate-400 shadow-sm">No hay tickets de soporte todavía.</div>
      ) : (
        <div className="space-y-3">
          {data.tickets.map((tk, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="admin-ticket-row">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-xs font-bold px-2 py-0.5">{tk.category}</span>
                  <h3 className="font-bold text-slate-900">{tk.subject}</h3>
                  {tk.has_screenshot && <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Paperclip size={13} /> captura</span>}
                  <span className={`inline-flex items-center rounded-full text-xs font-bold px-2 py-0.5 ${tk.status === "respondido" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {tk.status === "respondido" ? "Respondido" : "Abierto"}
                  </span>
                </div>
                <span className="text-xs text-slate-400">{fmtDate(tk.created_at)}</span>
              </div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{tk.message}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 border-t border-slate-100 pt-2">
                <span><b>{tk.name || "—"}</b> · <a href={`mailto:${tk.email}`} className="text-blue-600 hover:underline">{tk.email}</a></span>
                <span>Plan: <b>{tk.plan}</b></span>
                <span>Idioma: {tk.lang}</span>
                <span className={tk.email_sent ? "text-emerald-600" : "text-rose-600"}>{tk.email_sent ? "Email enviado ✓" : "Email NO enviado ✗"}</span>
              </div>
              {(tk.thread || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {tk.thread.map((m, mi) => (
                    <div key={mi} className={`rounded-lg border p-3 ${
                      m.from === "user" ? "bg-amber-50/60 border-amber-100" : "bg-blue-50/60 border-blue-100"}`}>
                      <div className={`flex items-center justify-between text-xs font-semibold mb-1 ${
                        m.from === "user" ? "text-amber-700" : "text-blue-700"}`}>
                        <span>{m.from === "user" ? `Respuesta de ${tk.name || tk.email}` : `Respuesta de ${m.admin_email || "admin"}`}</span>
                        <span className="font-normal opacity-60">{fmtDate(m.at)}</span>
                      </div>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">{m.message}</p>
                    </div>
                  ))}
                </div>
              )}
              <TicketReplyBox tk={tk} onReplied={onReplied} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KanbanCard({ card, done, onClick, draggable, onDragStart }) {
  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onClick={onClick}
      className={`rounded-lg border bg-white p-3 shadow-sm ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"} ${onClick ? "hover:border-blue-300" : ""}`}
    >
      <div className="text-sm font-semibold text-slate-900">{card.title}</div>
      {card.detail && card.detail !== card.title && (
        <div className="mt-1 line-clamp-3 text-xs text-slate-500">{card.detail}</div>
      )}
      {card.description && (
        <div className="mt-1 whitespace-pre-wrap text-xs text-slate-500">{card.description}</div>
      )}
      {card.dia && <div className="mt-2 text-[11px] font-medium text-slate-400">{card.dia}</div>}
    </div>
  );
}

// Kanban de MARKETING — editable de verdad (colección Mongo propia vía /admin/kanban/marketing).
// Mover una tarjeta entre columnas usa drag & drop nativo HTML5 (mismo patrón que el reordenado
// de clips de B-roll en TabBroll.jsx) — sin librería nueva.
function MarketingKanbanTab() {
  const [cards, setCards] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [showAdd, setShowAdd] = useState(null); // columna donde se está añadiendo, o null
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/kanban/marketing").then(({ data }) => setCards(data.cards)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const seedFromRoadmap = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/kanban/marketing/seed-from-roadmap");
      if (data.added > 0) toast.success(`${data.added} tarjetas importadas del roadmap.`);
      else toast.info("No hay tarjetas nuevas que importar.");
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSeeding(false); }
  };

  const byColumn = (col) => (cards || []).filter((c) => c.column === col);

  const moveCard = async (id, column) => {
    setCards((prev) => prev.map((c) => (c.id === id ? { ...c, column } : c)));
    try { await api.patch(`/admin/kanban/marketing/${id}`, { column }); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const createCard = async (column) => {
    if (!newTitle.trim()) { toast.warning("Escribe un título"); return; }
    setBusy(true);
    try {
      await api.post("/admin/kanban/marketing", { title: newTitle, description: newDesc, column });
      setNewTitle(""); setNewDesc(""); setShowAdd(null);
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const deleteCard = async (id) => {
    if (!window.confirm("¿Borrar esta tarjeta?")) return;
    try { await api.delete(`/admin/kanban/marketing/${id}`); setSelected(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const updateSelected = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/kanban/marketing/${selected.id}`, patch);
      setSelected(data);
      setCards((prev) => prev.map((c) => (c.id === data.id ? data : c)));
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!cards) return <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></div>;

  return (
    <div className="space-y-4" data-testid="admin-kanban-marketing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Acciones de marketing en curso. Arrastra una tarjeta a otra columna para moverla.</p>
        <button onClick={seedFromRoadmap} disabled={seeding} data-testid="kanban-seed-roadmap-btn"
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
          {seeding ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Importar del roadmap
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {KANBAN_COLUMN_ORDER.map((col) => (
          <div key={col}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (dragId) moveCard(dragId, col); setDragId(null); }}
            className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-3 min-h-[200px]">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">{KANBAN_COLUMN_LABELS[col]}</h3>
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">{byColumn(col).length}</span>
            </div>
            <div className="space-y-2">
              {byColumn(col).map((c) => (
                <KanbanCard key={c.id} card={c} done={col === "done"} draggable
                  onDragStart={() => setDragId(c.id)}
                  onClick={() => setSelected(c)} />
              ))}
            </div>
            {showAdd === col ? (
              <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-white p-2">
                <input autoFocus value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Título" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none" />
                <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={2}
                  placeholder="Descripción (opcional)" className="w-full rounded border border-slate-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none" />
                <div className="flex gap-2">
                  <button onClick={() => createCard(col)} disabled={busy}
                    className="flex-1 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60">Añadir</button>
                  <button onClick={() => { setShowAdd(null); setNewTitle(""); setNewDesc(""); }}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">Cancelar</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAdd(col)}
                className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 py-2 text-sm font-medium text-slate-500 hover:border-blue-400 hover:text-blue-600">
                <Plus size={15} /> Añadir tarjeta
              </button>
            )}
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start justify-between gap-4">
              <input value={selected.title} onChange={(e) => setSelected({ ...selected, title: e.target.value })}
                onBlur={(e) => updateSelected({ title: e.target.value })}
                className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-base font-bold focus:border-blue-500 focus:outline-none" />
              <button onClick={() => setSelected(null)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
            </div>
            <textarea value={selected.description} onChange={(e) => setSelected({ ...selected, description: e.target.value })}
              onBlur={(e) => updateSelected({ description: e.target.value })} rows={5}
              placeholder="Descripción" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none" />
            <div className="mt-3 flex items-center justify-between">
              <select value={selected.column} onChange={(e) => { const column = e.target.value; setSelected({ ...selected, column }); updateSelected({ column }); }}
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none">
                {KANBAN_COLUMN_ORDER.map((col) => <option key={col} value={col}>{KANBAN_COLUMN_LABELS[col]}</option>)}
              </select>
              <button onClick={() => deleteCard(selected.id)} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-rose-600 hover:bg-rose-50">
                <Trash2 size={15} /> Borrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Kanban de DESARROLLO — SOLO LECTURA, derivado de ROADMAP.md (/admin/kanban/dev). A propósito
// no tiene su propio almacén: el roadmap ya es la fuente de verdad de las tareas de desarrollo.
function DevKanbanTab() {
  const [cards, setCards] = useState(null);
  const [filter, setFilter] = useState("");
  useEffect(() => {
    api.get("/admin/kanban/dev").then(({ data }) => setCards(data.cards)).catch((e) => toast.error(apiErr(e)));
  }, []);
  if (!cards) return <div className="py-16 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={28} /></div>;

  const sections = [...new Set(cards.map((c) => c.section).filter(Boolean))];
  const filtered = filter ? cards.filter((c) => c.section === filter) : cards;
  const todo = filtered.filter((c) => !c.done);
  const done = filtered.filter((c) => c.done);

  return (
    <div className="space-y-4" data-testid="admin-kanban-dev">
      <p className="text-sm text-slate-500">
        Solo lectura — derivado en caliente de <code className="rounded bg-slate-100 px-1">ROADMAP.md</code>.
        Para cambiar el estado de una tarea, marca su checkbox en el roadmap (git).
      </p>
      <select value={filter} onChange={(e) => setFilter(e.target.value)}
        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none">
        <option value="">Todas las secciones</option>
        {sections.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {cards.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
          No se pudo leer ROADMAP.md (¿falta el bind mount en este entorno?).
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {[["todo", "Por hacer", todo], ["done", "Hecho", done]].map(([key, label, list]) => (
            <div key={key} className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-3">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-bold uppercase tracking-wide text-slate-600">{label}</h3>
                <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600">{list.length}</span>
              </div>
              <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
                {list.map((c, i) => <KanbanCard key={`${key}-${i}`} card={c} done={key === "done"} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Timestamp del último vistazo a las alertas, guardado en localStorage (por navegador/admin,
// no en el backend) — evita una colección nueva solo para un modelo de "leído/no leído".
const ALERTS_SEEN_KEY = "vb_admin_alerts_seen_at";

function AdminAlertsBell() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // id de la alerta abierta, o null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const since = localStorage.getItem(ALERTS_SEEN_KEY) || "";
      const { data } = await api.get("/admin/alerts", { params: since ? { since } : {} });
      setItems(data.items || []);
      setCount(data.count || 0);
    } catch (e) { /* silencioso — no bloquear el panel por esto */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      // Al abrir, marca como visto AHORA — la próxima carga solo trae lo posterior a este
      // instante. El badge desaparece aunque la lista siga mostrando lo recién visto.
      localStorage.setItem(ALERTS_SEEN_KEY, new Date().toISOString());
      setCount(0);
    }
  };

  return (
    <div className="relative">
      <button onClick={toggle} data-testid="admin-alerts-bell" aria-label="Alertas"
        className="relative grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">
        <Bell size={17} className={loading ? "animate-pulse" : ""} />
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-11 z-40 max-h-96 w-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl sm:w-96" data-testid="admin-alerts-dropdown">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-sm font-bold text-slate-900">Alertas (últimas 24h)</span>
              <button onClick={load} className="text-slate-400 hover:text-slate-700"><RefreshCw size={14} /></button>
            </div>
            {items.length === 0 ? (
              <p className="px-2 py-4 text-center text-sm text-slate-400">Sin errores recientes.</p>
            ) : (
              <div className="space-y-1">
                {items.map((a) => {
                  const isOpen = expanded === a.id;
                  const metaEntries = Object.entries(a.meta || {}).filter(([, v]) => v !== null && v !== "" && v !== undefined);
                  const hasDetail = a.error || metaEntries.length > 0;
                  return (
                    <button key={a.id} type="button" onClick={() => hasDetail && setExpanded(isOpen ? null : a.id)}
                      className={`w-full rounded-lg border border-rose-100 bg-rose-50 px-2.5 py-2 text-left text-xs ${hasDetail ? "cursor-pointer hover:bg-rose-100" : "cursor-default"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-rose-800">{EVENT_TYPE_LABELS[a.type] || a.type}</span>
                        <span className="shrink-0 text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString("es-ES")}</span>
                      </div>
                      {a.project_name && <div className="mt-0.5 text-slate-600">{a.project_name}</div>}
                      {a.error && (
                        <div className={`mt-0.5 text-slate-500 ${isOpen ? "whitespace-pre-wrap break-words" : "truncate"}`} title={isOpen ? undefined : a.error}>
                          {a.error}
                        </div>
                      )}
                      {isOpen && metaEntries.length > 0 && (
                        <div className="mt-1.5 space-y-0.5 rounded-md bg-white/70 p-1.5">
                          {metaEntries.map(([k, v]) => (
                            <div key={k} className="flex gap-1.5 text-[11px]">
                              <span className="shrink-0 font-semibold text-slate-500">{k}:</span>
                              <span className="break-words text-slate-700">{String(v)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {hasDetail && !isOpen && <div className="mt-0.5 text-[10px] font-semibold text-rose-400">Toca para ver más detalle</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Recuerda la pestaña vía ?tab= en la URL — así "Gestionar" en Usuarios puede volver aquí
  // (desde la página propia de un usuario) sin perder el contexto y caer siempre en Métricas.
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "metrics");

  if (user && user.role !== "admin") return <div className="grid min-h-screen place-items-center text-slate-500">Acceso restringido.</div>;

  const activeTab = TABS.find((t) => t.id === tab);

  return (
    <div className="min-h-screen bg-slate-50 md:flex md:h-screen md:overflow-hidden">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:sticky md:top-0 md:flex md:h-screen md:w-64 md:flex-shrink-0 md:flex-col md:border-r md:border-slate-200 md:bg-white">
        <div className="flex h-14 items-center gap-3 border-b border-slate-200 px-4">
          <button onClick={() => navigate("/app")} data-testid="admin-back-btn"
            className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0 leading-tight">
            <h1 className="truncate text-sm font-bold text-slate-900">Admin Zone</h1>
            <p className="truncate text-xs text-slate-500">YTMethod · Panel de control</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                tab === t.id ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <t.Icon size={17} className="flex-shrink-0" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="truncate border-t border-slate-200 px-4 py-3 text-xs text-slate-500">{user?.email}</div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:h-screen md:overflow-hidden">
        {/* Header (mobile: back+title+tabs; desktop: current section only) */}
        <header className="flex-shrink-0 border-b border-slate-200 bg-white">
          <div className="flex h-14 items-center justify-between px-4 md:px-6">
            <div className="flex items-center gap-3 md:hidden">
              <button onClick={() => navigate("/app")} data-testid="admin-back-btn-mobile"
                className="grid h-9 w-9 place-items-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100">
                <ArrowLeft size={18} />
              </button>
              <div className="leading-tight">
                <h1 className="text-sm font-bold text-slate-900">Admin Zone</h1>
                <p className="text-xs text-slate-500">YTMethod · Panel de control</p>
              </div>
            </div>
            <div className="hidden items-center gap-2 md:flex">
              {activeTab && <activeTab.Icon size={18} className="text-slate-400" />}
              <h2 className="text-base font-semibold text-slate-900">{activeTab?.label}</h2>
            </div>
            <div className="flex items-center gap-3">
              <AdminAlertsBell />
              <span className="hidden text-sm text-slate-500 sm:block md:hidden">{user?.email}</span>
            </div>
          </div>
          <div className="flex gap-1 overflow-x-auto px-4 pb-3 md:hidden">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-mobile-${t.id}`}
                className={`flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  tab === t.id ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>
                <t.Icon size={16} /> {t.label}
              </button>
            ))}
          </div>
        </header>

        <div className="w-full px-4 py-6 md:flex-1 md:overflow-y-auto md:px-6">
          {tab === "metrics" && <MetricsTab />}
          {tab === "funnel" && <MarketingFunnel />}
          {tab === "markup" && <MarkupTab />}
          {tab === "plans" && <PlansTab />}
          {tab === "stripe" && <StripePricesTab />}
          {tab === "ai-usage" && <AiUsageTab />}
          {tab === "topups" && <TopupsTab />}
          {tab === "seats" && <SeatsTab />}
          {tab === "logros" && <LogrosTab />}
          {tab === "referidos" && <ReferidosTab />}
          {tab === "users" && <UsersTab />}
          {tab === "videos" && <VideosTab />}
          {tab === "logs" && <LogsTab />}
          {tab === "activity" && <ActivityTab />}
          {tab === "errors" && <ErrorsTab />}
          {tab === "tickets" && <TicketsTab />}
          {tab === "growth" && <GrowthBoard />}
          {tab === "kanban-marketing" && <MarketingKanbanTab />}
          {tab === "kanban-dev" && <DevKanbanTab />}
          {tab === "prompts" && <PromptsTab />}
          {tab === "igreels" && <IgReelsBoard />}
        </div>
      </div>
    </div>
  );
}
